import { useMemo, useState } from "react";
import { AlertTriangle, Minus, Plus } from "lucide-react";
import { gbp } from "@/lib/api";

// Shared product display used by the public product page and the admin preview.
export const ProductView = ({ product, onAdd, preview = false }) => {
  const optionNames = product.option_names || [];
  const variants = product.variants || [];
  const [selected, setSelected] = useState({});
  const [qty, setQty] = useState(product.min_order_qty || 1);
  const [activeImage, setActiveImage] = useState(0);

  const variant = useMemo(() => {
    if (!optionNames.length) return null;
    if (!optionNames.every((n) => selected[n])) return undefined;
    return variants.find((v) => optionNames.every((n) => v.option_values?.[n] === selected[n])) || null;
  }, [selected, optionNames, variants]);

  const unitPrice = variant
    ? variant.effective_price_pence
    : product.sale_price_pence != null
      ? product.sale_price_pence
      : product.price_pence;

  const maxQty = product.max_qty_per_customer || 50;
  const hasOptions = optionNames.length > 0;
  const selectionIncomplete = hasOptions && variant === undefined;
  const canAdd = !preview && !product.sold_out && product.orderable !== false && !selectionIncomplete && (!hasOptions || variant !== null);
  const images = product.images || [];
  const current = images[activeImage];

  // Price table derived from the live variants (source of truth) — shown only when
  // the price depends purely on the first option dimension (e.g. Tubs, Box size).
  const priceTable = useMemo(() => {
    if (!optionNames.length) return null;
    const first = optionNames[0];
    const groups = new Map();
    for (const v of variants) {
      if (v.active === false) continue;
      const key = v.option_values?.[first];
      if (key == null || v.effective_price_pence == null) continue;
      if (!groups.has(key)) groups.set(key, new Set());
      groups.get(key).add(v.effective_price_pence);
    }
    const rows = [...groups.entries()];
    if (rows.length < 2) return null;
    if (rows.some(([, prices]) => prices.size !== 1)) return null;
    return rows.map(([label, prices]) => ({ label, price: [...prices][0] }));
  }, [optionNames, variants]);

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-2" data-testid="product-view">
      <div>
        <div className="overflow-hidden rounded-3xl border border-choco-line">
          {current ? (
            <img src={current.url} alt={current.alt || product.name} className="aspect-square w-full object-cover" data-testid="product-main-image" />
          ) : (
            <div className="flex aspect-square w-full items-center justify-center bg-choco-light text-sm text-cream-muted">Photo coming soon</div>
          )}
        </div>
        {current?.is_demo && (
          <p className="mt-2 text-xs italic text-cream-muted" data-testid="demo-image-note">
            Illustrative image — the exact bake may vary.
          </p>
        )}
        {images.length > 1 && (
          <div className="mt-3 flex gap-3" data-testid="product-thumbnails">
            {images.map((img, i) => (
              <button
                key={img.image_id || i}
                onClick={() => setActiveImage(i)}
                aria-label={`View image ${i + 1}`}
                className={`overflow-hidden rounded-2xl border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus ${
                  i === activeImage ? "border-gold" : "border-choco-line"
                }`}
                data-testid={`product-thumbnail-${i}`}
              >
                <img src={img.url_400 || img.url} alt="" className="h-16 w-16 object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <h1 className="font-serif text-3xl font-bold tracking-tight text-cream-text sm:text-4xl" data-testid="product-name">
          {product.name}
        </h1>
        {product.short_description && (
          <p className="mt-3 text-base leading-relaxed text-cream-muted">{product.short_description}</p>
        )}

        <div className="mt-5 flex items-baseline gap-3">
          <p className="font-serif text-3xl font-bold text-gold" data-testid="product-price">{gbp(unitPrice)}</p>
          {product.sale_price_pence != null && !variant && (
            <p className="text-lg text-cream-muted line-through">{gbp(product.price_pence)}</p>
          )}
        </div>

        {priceTable && (
          <div className="mt-4 rounded-2xl border border-choco-line bg-choco-light/50 p-4" data-testid="bundle-price-table">
            <h2 className="text-xs font-bold uppercase tracking-wide text-cream-muted">Price options</h2>
            <ul className="mt-2 space-y-1.5">
              {priceTable.map((row) => (
                <li
                  key={row.label}
                  className="flex items-baseline justify-between gap-3 text-sm"
                  data-testid={`bundle-price-row-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <span className="text-cream-text">{row.label}</span>
                  <span className="shrink-0 font-bold text-gold">{gbp(row.price)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {product.dietary_labels?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="dietary-labels">
            {product.dietary_labels.map((l) => (
              <span key={l} className="rounded-full border border-gold/50 px-3 py-1 text-xs font-bold capitalize text-gold">{l}</span>
            ))}
          </div>
        )}

        {optionNames.map((name) => {
          const values = [...new Set(variants.map((v) => v.option_values?.[name]).filter(Boolean))];
          return (
            <fieldset key={name} className="mt-6">
              <legend className="mb-2 text-sm font-semibold text-cream-text">{name}</legend>
              <div className="flex flex-wrap gap-2" data-testid={`option-group-${name.toLowerCase().replace(/\s+/g, "-")}`}>
                {values.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setSelected((s) => ({ ...s, [name]: value }))}
                    aria-pressed={selected[name] === value}
                    className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus ${
                      selected[name] === value
                        ? "bg-gold text-choco"
                        : "border border-choco-line text-cream-muted hover:border-gold hover:text-gold"
                    }`}
                    data-testid={`option-${name.toLowerCase().replace(/\s+/g, "-")}-${value.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </fieldset>
          );
        })}
        {selectionIncomplete && (
          <p className="mt-2 text-sm text-cream-muted" data-testid="select-option-hint">Please choose your options.</p>
        )}
        {variant === null && !selectionIncomplete && optionNames.length > 0 && (
          <p className="mt-2 text-sm font-semibold text-red-400" data-testid="option-unavailable">That combination isn't available — try another.</p>
        )}

        <div className="mt-6 flex items-center gap-4">
          <div className="flex items-center rounded-full border border-choco-line">
            <button
              type="button"
              onClick={() => setQty((q) => Math.max(product.min_order_qty || 1, q - 1))}
              aria-label="Decrease quantity"
              className="p-3 text-cream-text transition-colors hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus rounded-full"
              data-testid="qty-decrease-button"
            >
              <Minus className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="w-10 text-center text-sm font-bold text-cream-text" data-testid="qty-value">{qty}</span>
            <button
              type="button"
              onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
              aria-label="Increase quantity"
              className="p-3 text-cream-text transition-colors hover:text-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus rounded-full"
              data-testid="qty-increase-button"
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {!preview && (
            <button
              type="button"
              disabled={!canAdd}
              onClick={() => onAdd(variant, qty, unitPrice)}
              className="flex-1 rounded-full bg-gold px-8 py-3.5 text-base font-bold text-choco transition-colors duration-150 hover:bg-gold-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-focus focus-visible:ring-offset-2 focus-visible:ring-offset-choco active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="add-to-basket-button"
            >
              {product.sold_out ? "Sold Out" : product.orderable === false ? "Not yet available" : `Add to Basket — ${gbp(unitPrice * qty)}`}
            </button>
          )}
        </div>
        {product.orderable === false && !product.sold_out && (
          <p className="mt-2 text-xs text-cream-muted" data-testid="not-yet-available-note">
            This bake isn't available to order yet — its details are being finalised. Check back soon.
          </p>
        )}
        {(product.min_order_qty > 1 || product.max_qty_per_customer) && (
          <p className="mt-2 text-xs text-cream-muted" data-testid="qty-limits-note">
            {product.min_order_qty > 1 && `Minimum order: ${product.min_order_qty}. `}
            {product.max_qty_per_customer && `Maximum per customer: ${product.max_qty_per_customer}.`}
          </p>
        )}

        <div className="mt-8 rounded-3xl border border-gold/40 bg-choco-light p-5" data-testid="allergen-panel">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" aria-hidden="true" />
            <div>
              <h2 className="text-sm font-bold text-cream-text">Allergens</h2>
              {product.allergen_declaration_status === "none_of_14_as_ingredients" ? (
                <p className="mt-1 text-sm leading-relaxed text-cream-muted" data-testid="allergen-none-declared">
                  No UK 14 regulated allergens are used as ingredients.
                </p>
              ) : product.allergens?.length > 0 ? (
                <p className="mt-1 text-sm capitalize leading-relaxed text-cream-muted" data-testid="allergen-list">
                  Contains: {product.allergens.join(", ")}.
                </p>
              ) : (
                <p className="mt-1 text-sm leading-relaxed text-cream-muted" data-testid="allergen-missing">
                  Allergen information for this bake is being confirmed — please contact us before ordering if you have an allergy.
                </p>
              )}
              {product.may_contain?.length > 0 && (
                <p className="mt-2 text-sm capitalize leading-relaxed text-amber-300" data-testid="may-contain-note">
                  May contain: {product.may_contain.join(", ")} due to preparation in a kitchen that handles these allergens.
                </p>
              )}
              {product.additive_warnings?.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="additive-warnings">
                  {product.additive_warnings.map((w, i) => (
                    <p key={i} className="text-xs leading-relaxed text-amber-300" data-testid={`additive-warning-${i}`}>
                      {w}
                    </p>
                  ))}
                </div>
              )}
              {!product.allergen_information_verified && product.allergens?.length > 0 && (
                <p className="mt-1 text-xs italic text-cream-muted" data-testid="allergen-unverified-note">
                  Allergen details are being confirmed by the bakery — if you have an allergy, please contact us before ordering.
                </p>
              )}
              <p className="mt-2 text-xs leading-relaxed text-cream-muted">
                Made in a kitchen that handles common allergens. If you have an allergy, please contact us before ordering.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-5 text-sm leading-relaxed text-cream-muted" data-testid="product-details">
          {product.full_description && <p>{product.full_description}</p>}
          {product.ingredients && (
            <div>
              <h2 className="font-semibold text-cream-text">Ingredients</h2>
              <p className="mt-1">{product.ingredients}</p>
            </div>
          )}
          {(product.storage_instructions || product.shelf_life || product.reheat_instructions) && (
            <div className="space-y-2">
              {product.storage_instructions && <p><strong className="text-cream-text">Storage:</strong> {product.storage_instructions}</p>}
              {product.shelf_life && <p><strong className="text-cream-text">Shelf life:</strong> {product.shelf_life}</p>}
              {product.reheat_instructions && <p><strong className="text-cream-text">To enjoy warm / bake:</strong> {product.reheat_instructions}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
